import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { useGetUserByEmail } from "~/screens/Auth/hooks/useGetUserByEmail";
import { patientScreeningSchema } from "~/services";
import type { User } from "~/shared.types";
import { Input } from "~/ui";
import { FieldError } from "~/ui/common/FieldError";
import { FieldHint } from "../components/field-hint";
import { StaffBookingFooter } from "../components/staff-booking-footer";
import { StepHeading } from "../components/step-heading";

const emailSchema = patientScreeningSchema.pick({ email: true });
type EmailFormData = z.infer<typeof emailSchema>;

const EMAIL_FORM_ID = "staff-booking-email-form";
const EMAIL_ERROR_ID = "staff-booking-email-error";

interface EmailStepProps {
  defaultEmail: string;
  onContinue: (email: string) => void;
  onCancel: () => void;
}

export const EmailStep = ({
  defaultEmail,
  onContinue,
  onCancel,
}: EmailStepProps) => {
  const navigateModal = useNavigateModal();
  const [existingUser, setExistingUser] = useState<{
    email: string;
    user: User;
  } | null>(null);
  const [checkFailed, setCheckFailed] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: defaultEmail },
  });

  const email = watch("email");

  const { mutate: checkUserByEmail, isPending: isCheckingEmail } =
    useGetUserByEmail();

  const conflictingUser =
    existingUser && existingUser.email === email ? existingUser.user : null;

  const emailError =
    errors.email?.message ??
    (conflictingUser
      ? "An account already uses this email address."
      : checkFailed
        ? "We couldn't check this email. Please try again."
        : undefined);

  const onSubmit = ({ email }: EmailFormData) => {
    setExistingUser(null);
    setCheckFailed(false);

    checkUserByEmail(encodeURIComponent(email), {
      onSuccess: (user) => setExistingUser({ email, user }),
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          onContinue(email);
          return;
        }

        setCheckFailed(true);
      },
    });
  };

  return (
    <div className="flex min-h-full flex-col gap-6">
      <StepHeading title="Register a new patient" />

      <form id={EMAIL_FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}>
        <Input
          id="staff-booking-email"
          label="Patient email address"
          inputType="email"
          placeholder="jane.smith@example.com"
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          required
          isError={Boolean(emailError)}
          aria-describedby={emailError ? EMAIL_ERROR_ID : undefined}
          {...register("email", { onChange: () => setCheckFailed(false) })}
        />
        {emailError ? (
          <FieldError id={EMAIL_ERROR_ID}>{emailError}</FieldError>
        ) : (
          <FieldHint>
            This helps us identify if a user already exists under this email.
          </FieldHint>
        )}
        {conflictingUser && (
          <button
            type="button"
            className="text-violet cursor-pointer pt-2 text-sm underline"
            onClick={() =>
              navigateModal(
                `${MODAL_ROUTES.userDetails}/${conflictingUser.id}`,
                {},
                { replace: true },
              )
            }
          >
            {`Open ${conflictingUser.firstName} ${conflictingUser.lastName} in User Details`}
          </button>
        )}
      </form>

      <StaffBookingFooter
        onCancel={onCancel}
        continueProps={{
          type: "submit",
          form: EMAIL_FORM_ID,
          disabled: !email || isCheckingEmail || !!conflictingUser,
          isLoading: isCheckingEmail,
        }}
      />
    </div>
  );
};
